import _ from 'lodash';
import MobileFiles from '../MobileFiles';
import logErrorForCloudWatch from '../awsCloudwatchLogs';

/**
 * FileUtil: 
 * Utility for manipulating files and file metadata
 */
import dataModel from '../../models/DataModel';
import PhotoUtil from '../PhotoUtil/PhotoUtil';

const extensionTypes = {
  pdfTypes: '.pdf',
  excelTypes: '.xlsx, .xls, .xlsm',
  docTypes: '.docx, .doc, .rtf, .txt, .csv',
  photoTypes: '.png, .jpg, .jpeg, .webp',
  movieTypes: '.mov, .mp4, .m4v, .3gp',
  allTypes: '.png, .jpg, .jpeg, .webp, .pdf, .docx, .xlsx, .xls, .xlsm, .txt, .docx, .doc, .rtf, .mov, .mp4, .m4v, .3gp',
};

function getExtension(fileName) {
  if(fileName){
    const splitName = fileName.split('.');
    let extension = (splitName.length > 1) ? splitName.pop().toLowerCase() : null;

    if (extension) {
      // NOTE: android likes to send urls of the sort "*.png?123456" so
      // we remove any extraneous characters from the extension
      let markStart = extension.indexOf('?');
      if (markStart >= 0) {
        extension = extension.substring(0, markStart);
      }
      // check for forward slash in extension... this should handle filenames
      // similar to "X/X/x.y/Z" with no real extension... without this check
      // extension would get set to "y/Z"
      return extension.indexOf('/') >= 0 ? null : extension;
    }
  }
  return null;
}

function trimExtension(fileName) {
  const splitName = fileName.split('.');
  //remove extension
  if (splitName.length > 1) {
    splitName.pop();
  }
  return splitName.join('.');
}

function getAcceptedExtensionsByType(type, trimPeriod, asArray) {
  type = type || '';
  let acceptedExtensions = '';
  switch(type.toLowerCase()){
  case 'pdf':
    acceptedExtensions = extensionTypes.pdfTypes; 
    break;
  case 'xlsx':
  case 'xls':
  case 'xlsm':
    acceptedExtensions = extensionTypes.excelTypes;
    break;
  case 'docx':
  case 'doc':
    acceptedExtensions = extensionTypes.docTypes;  
    break;
  case 'jpg':
  case 'jpeg':
  case  'png':
    acceptedExtensions = extensionTypes.photoTypes;
    break;
  case 'mp4':
    acceptedExtensions = extensionTypes.movieTypes;
    break;
  case 'other':
  case 'Other':
    acceptedExtensions = '';
    break;
  default:
    acceptedExtensions = extensionTypes.allTypes;
  }
  if (trimPeriod) {
    acceptedExtensions = _.replace(acceptedExtensions, /\./g, '');
  }
  if (asArray) {
    acceptedExtensions = acceptedExtensions.split(', ');
  }
  return acceptedExtensions;
}

/** Do not include the extension when calling this method! (. is not allowed in name) */
function nameIsValid(fileName) {
  // must be something...
  if (!fileName || fileName.trim().length == 0) {
    return false;
  }
  // Check for special characters
  const regex = /[\\\/:;{}[\]<>'"~\.,!@#\$%&\*]/g;
  return !regex.test(fileName);
}

function replaceSpecialCharacters(fileName) {
  return fileName.replace(/[/[\\\/:;{}[\]<>'"~\.,!@#\$%&\*\s/]/g, '_');
}

function _logMessage(message) {
  const appState = dataModel.store.getState();
  let { currentCompany } = appState.new_companies;
  let { currentUser } = appState.new_users;

  const onMobile = dataModel.mobileBridge.isMobile;

  const errorInfoObject = {
    spaVersion: process.env.VERSION,
    type: 'DEBUG',
    message,
    currentUser,
    currentCompany,
    onMobile,
    source: null,
  };

  console.log(message);
  logErrorForCloudWatch(errorInfoObject);
}

async function handleMobileFileUpload(completionStep, task, setLoading, updateCallback) {
  try {
    // this takingPicture nonsense is to prevent app resumes while this process
    // is underway from pushing url onto the stack (an Android issue)
    localStorage.takingPicture = true;
    let uri = null;
    let extension = null;
    let mimeType = null;
    if (completionStep.requested_file_type == 'mp4') {// && !(window.cordova.platformId && window.cordova.platformId == 'androidd')) {
      console.log('using camera');
      const results = await _getVideo();
      if (!results) {
        return;
      }
      console.log(results);
      uri = results.filename;
      extension = getExtension(uri);
      if (updateCallback) {
        updateCallback('persist', null);
      }
      uri = await _persistFile(uri);
    }
    else {
      console.log('using chooser');
      const rawFile = await window.plugins.chooser.getFile();
      if (!rawFile) {
        return;
      }
      console.log(rawFile);
      extension = getExtension(rawFile.name);
      const acceptedTypes = getAcceptedExtensionsByType(completionStep.requested_file_type);
      const isOtherCS = completionStep.requested_file_type.toLowerCase() == 'other';
      // if the completion step type is not other and the uploaded file does not match the CS type, show error
      if (!isOtherCS && !acceptedTypes.includes(extension)) {
        dataModel.showGlobalModal('SYSTEM_POPUP', {
          message: `Only files with the extensions ${acceptedTypes} are allowed on this Completion Step.`,
          okButtonText: 'OK',
          oneButton: true,
          width: 320,
          captionText: 'ERROR',
          handleOwnerOk: () => { },
        });
        return;
      }
      uri = rawFile.uri;
      if (updateCallback) {
        updateCallback('persist', null);
      }
      uri = await _persistFile(uri);
      mimeType = rawFile.mediaType;
    }
    delete localStorage.takingPicture;
    console.log(uri);
    console.log(mimeType);
    window.resolveLocalFileSystemURL(uri, async (entry) => {
      console.log(entry);
      const fileData = {
        uri: entry.nativeURL,
        name: entry.name,
        mime_type: mimeType,
        progress: null,
      };
      console.log('resolved!');
      const updatedStep = await PhotoUtil.handleSaveFile(extension, fileData, null, completionStep, task);
      if (updateCallback) {
        updateCallback('created', updatedStep);
      }
    },
      (error) => {
        console.log(error);
        _logMessage('resolve failed for ' + uri);
        if (updateCallback) {
          updateCallback('error', error);
        }
      });
  } catch (e) {
    if (updateCallback) {
      updateCallback('error', e);
    }
    console.log(e);
  }
}

function _getVideo() {
  return new Promise((resolve, reject) => {
    const cameraOptions = { sourceType: 0, destinationType: 1, mediaType: 1 };
    navigator.camera.getPicture((results) => {
      console.log(results);
      try {
        results = JSON.parse(results);
      } catch (e) {
        results = { filename: results };
      }
      resolve(results);
    },
      (error) => {
        // sigh... it appears the plugins return an error when you cancel
        // out of a video upload (WTF? no way to distinguish a real error
        // vs cancel without hacking the plugin) so we will resolve with null
        resolve(null);
      },
      cameraOptions);
  });
}

async function _persistFile(uri) {
  try {
    _logMessage('trying to move ' + uri);
    uri = await MobileFiles.persistFile(uri);
    _logMessage('moved ' + uri);
  } catch (e) {
    // assume file is not local... 
    _logMessage('move failed ' + uri);
    _logMessage('trying to copy ' + uri);
    try {
      uri = await MobileFiles.persistFile(uri, true);
      _logMessage('copied ' + uri);
    } catch (e2) {
      _logMessage('copy failed ' + uri);
      _logMessage('using uri as is (may not work)');
    }
  }
  return uri;
}

export default {
  getExtension,
  trimExtension,
  getAcceptedExtensionsByType,
  nameIsValid,
  replaceSpecialCharacters,
  extensionTypes,
  handleMobileFileUpload,
};



// WEBPACK FOOTER //
// ./src/helpers/FileUtil/FileUtil.js